import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface Assurance {
  id: string;
  nom: string;
  description: string;
  actif: boolean;
}

export default function AdminAssurancesPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [assurances, setAssurances] = useState<Assurance[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNom, setNewNom] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editNom, setEditNom] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'super_admin' && role !== 'admin') { navigate('/', { replace: true }); return; }
    loadAssurances();
  }, [user, role, authLoading]);

  const loadAssurances = async () => {
    const { data } = await supabase.from('assurances').select('*').order('nom');
    if (data) setAssurances(data);
    setLoading(false);
  };

  const addAssurance = async () => {
    if (!newNom.trim()) { setError('Le nom est obligatoire'); return; }
    try {
      const { error: insertErr } = await supabase.from('assurances').insert({ nom: newNom.trim(), description: newDesc.trim() || null });
      if (insertErr) throw insertErr;
      setSuccess('Assurance ajoutée');
      setNewNom(''); setNewDesc('');
      loadAssurances();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const toggleActif = async (id: string, actif: boolean) => {
    await supabase.from('assurances').update({ actif: !actif }).eq('id', id);
    loadAssurances();
  };

  const startEdit = (a: Assurance) => {
    setEditing(a.id);
    setEditNom(a.nom);
    setEditDesc(a.description || '');
  };

  const saveEdit = async () => {
    if (!editNom.trim()) return;
    await supabase.from('assurances').update({ nom: editNom.trim(), description: editDesc.trim() || null }).eq('id', editing!);
    setEditing(null);
    loadAssurances();
    setSuccess('Assurance modifiée');
    setTimeout(() => setSuccess(''), 3000);
  };

  const deleteAssurance = async (id: string) => {
    if (!confirm('Supprimer cette assurance ?')) return;
    await supabase.from('assurances').delete().eq('id', id);
    loadAssurances();
    setSuccess('Assurance supprimée');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center"><i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i><p>Chargement...</p></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-xl border-2 border-teal-100 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Gestion des Assurances</h1>
                <p className="text-gray-600 mt-2">Gérez la liste des assurances partenaires</p>
              </div>
              <button onClick={() => navigate('/admin')} className="px-4 py-2 text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">← Retour</button>
            </div>

            {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-2xl flex items-start gap-2"><i className="ri-error-warning-line mt-0.5 w-5 h-5 flex items-center justify-center"></i><span>{error}</span></div>}
            {success && <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-600 rounded-2xl flex items-start gap-2"><i className="ri-check-line mt-0.5 w-5 h-5 flex items-center justify-center"></i><span>{success}</span></div>}

            {/* Formulaire d'ajout */}
            <div className="mb-8 p-6 bg-teal-50 border-2 border-teal-200 rounded-2xl">
              <h3 className="text-lg font-semibold mb-4">Ajouter une assurance</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input type="text" value={newNom} onChange={(e) => setNewNom(e.target.value)} placeholder="Nom de l'assurance" className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm" />
                <input type="text" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description (optionnelle)" className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm" />
                <button onClick={addAssurance} className="px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 font-semibold whitespace-nowrap cursor-pointer">Ajouter</button>
              </div>
            </div>

            {/* Liste */}
            <div className="space-y-3">
              {assurances.map((a) => (
                <div key={a.id} className={`p-4 rounded-xl border-2 transition-all ${a.actif ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
                  {editing === a.id ? (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input type="text" value={editNom} onChange={(e) => setEditNom(e.target.value)} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500" />
                      <input type="text" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500" />
                      <button onClick={saveEdit} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap">Enregistrer</button>
                      <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap">Annuler</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{a.nom}</p>
                        {a.description && <p className="text-sm text-gray-500">{a.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleActif(a.id, a.actif)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap ${a.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {a.actif ? 'Actif' : 'Inactif'}
                        </button>
                        <button onClick={() => startEdit(a)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 cursor-pointer"><i className="ri-edit-line"></i></button>
                        {isTrueSuperAdmin && <button onClick={() => deleteAssurance(a.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer"><i className="ri-delete-bin-line"></i></button>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}